import dayjs from "dayjs";

import { useMemo } from "react";

import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { Card, Chip, Grid, Tooltip, Typography } from "@mui/material";

import { ApiDealsRoutesItem } from "@/api/routes";
import { AIRPORT_MAP } from "@/constants/airport";
import airportTags from "@/constants/airportTags";
import { CabinMap } from "@/constants/config";
import useThemeData from "@/hooks/useThemeData";

import Banks from "../Banks";

const RoutesItem = (props: {
  data: ApiDealsRoutesItem;
  onClick: (v: ApiDealsRoutesItem) => void;
  onAlertClick: (v: ApiDealsRoutesItem) => void;
}) => {
  const { data } = props;
  const theme = useThemeData();

  const route = data.route.split("-");
  const origin = route[0];
  const to = route[1];

  const tags = useMemo(() => {
    const airports = data.route.split("-").slice(1, 2);
    return airportTags
      .filter((item) => {
        return item.airports.some((idx) => airports.includes(idx));
      })
      .map((item) => ({ label: item.name, icon: item.icon, value: item.name }))
      .slice(0, 3);
  }, [data]);
  return (
    <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
      <Card
        style={{
          borderColor: theme.palette.divider,
          borderWidth: 1,
          borderStyle: "solid",
          background: "none",
          borderRadius: "16px",
          transition: "box-shadow 0.3s ease-in-out",
        }}
        elevation={2}
        className="rounded-[6px] cursor-pointer hover:shadow-lg relative"
        onClick={() => props.onClick(data)}
        sx={{
          "&:hover": {
            elevation: 6,
            boxShadow: 6,
          },
        }}
      >
        <div className="absolute top-[10px] left-[10px] right-[10px] flex justify-between items-center">
          <div className="flex gap-[4px]">
            {tags.map((item, index) => (
              <Tooltip
                key={index}
                title={
                  <div className="flex items-center gap-1">
                    <item.icon color="primary" fontSize="small" />
                    {item.label}
                  </div>
                }
                placement="top"
                arrow
              >
                <div
                  key={index}
                  className="text-white rounded-full bg-black/40 p-2 h-fit text-[0px]"
                >
                  {<item.icon />}
                </div>
              </Tooltip>
            ))}
          </div>
          <div className="flex gap-1">
            <div className="text-white rounded-lg bg-black/40 p-2 text-xs font-semibold">
              {data.stop_count == 0
                ? "Nonstop"
                : data.stop_count == 1
                  ? "1 stop"
                  : `${data.stop_count} stops`}
            </div>
            <div className="text-white rounded-lg bg-black/40 p-2 text-xs font-semibold">
              {CabinMap[data.target_cabin]}
            </div>
          </div>
        </div>
        <div
          className="rounded-tl-[2px] rounded-bl-[2px] h-[190px] bg-no-repeat bg-center bg-cover"
          style={{
            backgroundImage: `url(${data.city_img})`,
          }}
        />
        <div className="p-[10px] flex-1">
          <div className="flex justify-between items-center">
            <Typography fontSize={17} color="textPrimary" noWrap fontWeight="bold">
              {`${origin} - ${AIRPORT_MAP[to].city}(${to})`}
            </Typography>
            <div>
              <NotificationsActiveIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
                sx={{ verticalAlign: "middle", ml: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onAlertClick?.(data);
                }}
              />
            </div>
          </div>
          <div className="flex items-center md:space-x-[15px] space-x-[5px] my-[1px] !mt-[2px]">
            <Typography fontSize={15} color="textPrimary">
              {data.date} | {CabinMap[data.target_cabin]} |{" "}
              {data.stop_count == 0
                ? "nonstop"
                : data.stop_count == 1
                  ? "1 stop"
                  : `${data.stop_count} stops`}
            </Typography>
          </div>
          <div className="flex items-center md:space-x-[15px] space-x-[5px]">
            <Typography fontSize={17} color="primary" fontWeight={600} className="font-bold">
              From {data.price >= 1000 ? `${(data.price / 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K` : data.price.toLocaleString()} pts
              {data.tax
                ? " +$" +
                data.tax.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })
                : ""}
              {data.ss > 0 ? (
                <Chip
                  size="small"
                  style={{ marginLeft: 10, paddingLeft: 5, paddingRight: 5 }}
                  color="primary"
                  label={`${data.ss}${data.ss == 9 ? "+" : ""} ${data.ss == 1 ? "seat" : "seats"}`}
                />
              ) : (
                <span />
              )}
            </Typography>
          </div>

          <Typography fontSize={15} className="!md:mt-[10px] !mt-[0px]">
            Last Seen: {data.ls ? dayjs(data.ls * 1000).fromNow() : "few days ago"}
          </Typography>

          <div className="md:mt-[10px] mt-[0px]">
            <Banks data={data} />
          </div>
        </div>
      </Card>
    </Grid>
  );
};

export default RoutesItem;
